import type { FC, KeyboardEvent, ReactNode } from 'react';
import type { IEvaultApi } from './use-evault-api';
import type {
	IDocumentState,
	IDriveView,
	IVault,
	IVaultDocument,
} from './types';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import { useNotification } from 'hooks';
import { Kebab, Pager, SearchBox, StatusChip } from 'views/design-system';

import { AddonUpsellModal } from './addon-upsell-modal';
import { CheckoutModal } from './checkout-modal';
import { DocumentDrawer } from './document-drawer';
import { RevaultModal } from './revault-modal';
import { TransferModal } from './transfer-modal';
import {
	dayOf,
	formatDisplayId,
	isoOf,
	previewLineWidths,
	registryChip,
	timeOf,
} from './derive';
import { useVaultDocuments } from './use-evault-deck';

/** The ledger anchor state, beside "Retained copy" in both the list row and the
    grid card. `StatusChip` takes no `title`, so the sentence that explains the
    three words rides on a wrapper. No state to report — the common case, and
    every document while the feature is off — renders nothing at all. */
const LedgerChip: FC<{ doc: IVaultDocument }> = ({ doc }) => {
	const chip = registryChip(doc);
	if (!chip) return null;
	return (
		<span title={chip.title}>
			<StatusChip verdict={chip.verdict}>{chip.label}</StatusChip>
		</span>
	);
};

const SORTS: { key: string; label: string; sort: 'depositedAt' | 'name'; order: 'asc' | 'desc' }[] =
	[
		{ key: 'recent', label: 'Recently added', sort: 'depositedAt', order: 'desc' },
		{ key: 'oldest', label: 'Oldest first', sort: 'depositedAt', order: 'asc' },
		{ key: 'name', label: 'Name A→Z', sort: 'name', order: 'asc' },
	];

/**
 * The two states this panel can list (SIM-23).
 *
 * A pill rather than a tab strip, and it sits beside the sort pill rather than above the
 * table, because it is the same kind of control: it changes what the server is asked
 * for. There is no `all` option — a mixed list would show rows whose actions differ with
 * nothing on screen explaining why.
 */
const STATES: { key: IDocumentState; label: string }[] = [
	{ key: 'vaulted', label: 'In vault' },
	{ key: 'checked_out', label: 'Checked out' },
];

const NEEDS_ADDON_TRANSFER = 'Transfers need the eVault add-on.';
const NEEDS_ADDON_CHECKOUT = 'Checkouts need the eVault add-on.';
/* A checked-out original is not the vault's to hand over any more, and the server
   refuses the initiate. Saying so on the disabled item is the difference between a
   control that looks broken and one that has told you why. */
const CHECKED_OUT_HINT = 'Checked out of the vault';
/* The legal hold is a records-management decision that outranks custody: the record
   cannot leave while it stands, which is the whole purpose of placing one. */
const LEGAL_HOLD_HINT = 'Under legal hold — release it first';
/* A retention copy is not ours to transfer: the original left with the new
   owner, and the server refuses the initiate (`ownerId` scope). Offering the
   action would only send the admin to a 403. */
const RETAINED_COPY_HINT =
	'Transferred away — you kept a copy. Only the current owner can transfer it.';
/* The same rule, a DIFFERENT action — and a hint has to name the action it hangs
   off. Reusing the transfer sentence on Check out is what made SIM-33 read as a
   broken button: the reporter clicked Check out, was told who may TRANSFER the
   document, and concluded the control had failed rather than that it was
   refusing on purpose. */
const RETAINED_COPY_CHECKOUT_HINT =
	'Transferred away — you kept a copy. Only the current owner can check it out.';

export interface IDocumentPanelProps {
	api: IEvaultApi;
	/** Which vaults' documents to union. Empty means: issue no request at all. */
	vaultIds: string[];
	/** The vault page already knows which vault it is in. */
	hideVaultColumn?: boolean;
	view: IDriveView;
	/**
	 * `false` means the server refused entitlement; `null`/undefined means the
	 * probe never answered, and an unanswered probe is not a refusal. Passed in
	 * rather than probed here: both decks already hold the answer, and a third
	 * `GET /vault-entitlement` per page would buy nothing.
	 */
	blocked?: boolean;
	/**
	 * Mirrored up so a bulk bar drawn OUTSIDE this panel — the drive's, which
	 * also counts vaults — can read the selection, clear it, and open THIS
	 * panel's transfer modal.
	 *
	 * The three travel together on purpose. An `ids: string[]` mirror was enough
	 * while the bar only had to show a number; a transfer needs the document
	 * OBJECTS (the recipient list is the intersection of their signers), and
	 * opening a second copy of the modal upstream would mean two components
	 * racing to refetch the same list afterwards.
	 */
	onSelectionChange?: (selection: {
		documents: IVaultDocument[];
		openTransfer: () => void;
		clear: () => void;
	}) => void;
	/**
	 * Dropped into this panel's own control strip, between the sort select and
	 * the pager. The drive carries the list/grid toggle in the VAULT strip above
	 * (one switch for both halves of that page); the vault page has only this
	 * strip, so it hands its toggle down here rather than growing a second
	 * control row for one button pair.
	 */
	extraControls?: ReactNode;
	/**
	 * The vault page's bulk bar, rendered above the panel with this component's
	 * own selection.
	 *
	 * A render prop rather than a mirror threaded back down, because the
	 * selection LIVES here. The vault page's bar is only about documents, so it
	 * is drawn inside this panel with the state in hand; the drive's bar also
	 * counts vaults, so that one is drawn upstream and reaches the same handles
	 * through `onSelectionChange`.
	 */
	bulkBar?: (ctx: {
		selectedDocs: IVaultDocument[];
		clear: () => void;
		openTransfer: () => void;
	}) => ReactNode;
	/** The server-owned per-document transfer price, passed down to the transfer modal. */
	transferPrice: number;
	/**
	 * The vaults this deck already loaded, for the re-vault modal's picker.
	 *
	 * Optional because the two callers have different answers: the drive holds the whole
	 * list, the vault page holds exactly one and therefore has nothing to choose between
	 * — and the modal draws no picker rather than a select with one option in it.
	 */
	vaults?: Pick<IVault, '_id' | 'name'>[];
}

/**
 * The Documents half of the drive, and the whole body of the vault page.
 *
 * One component for both because it is one endpoint for both
 * (`GET /vault-documents?vaultIds=`): the drive passes its vault selection and
 * gets the union, the vault page passes a single id. The only difference is the
 * Vault column, which is provenance the vault page does not need.
 *
 * Documents cannot be uploaded (FR-V7), so there is no empty-state CTA here —
 * the deposit note under the drive says how they arrive instead.
 */
export const DocumentPanel: FC<IDocumentPanelProps> = ({
	api,
	vaultIds,
	hideVaultColumn,
	view,
	blocked,
	onSelectionChange,
	extraControls,
	bulkBar,
	transferPrice,
	vaults,
}) => {
	const { successNotification } = useNotification();
	const {
		typedSearch,
		setTypedSearch,
		filters,
		setFilter,
		page,
		setPage,
		pages,
		documents,
		refetchDocuments,
		selectedIds,
		toggleDocument,
		toggleAllDocuments,
		clearDocuments,
		activeFilterCount,
	} = useVaultDocuments(api, vaultIds);

	/* The drawer's document and the transfer's documents, kept apart: the drawer
	   is opened on one row and the modal may be opened on a whole selection, and
	   collapsing them into one piece of state would mean the drawer's footer
	   button silently retargeted whatever the checkboxes happened to hold. */
	const [openDoc, setOpenDoc] = useState<IVaultDocument | null>(null);
	const [transferDocs, setTransferDocs] = useState<IVaultDocument[] | null>(null);
	/* Both are ONE document, never the checkbox selection: a checkout hands one sealed
	   file to one person, and a re-vault deposits one set of retained bytes. */
	const [checkoutDoc, setCheckoutDoc] = useState<IVaultDocument | null>(null);
	const [revaultDoc, setRevaultDoc] = useState<IVaultDocument | null>(null);
	const [upsellOpen, setUpsellOpen] = useState(false);

	/* Resolved from the rows on screen, so the transfer modal reads real
	   `signers[]` rather than ids it would have to fetch back. A document
	   selected on a page the reader has since left is not in `items` any more,
	   which is the same thing the server would say about it. */
	const selectedDocs = useMemo(
		() =>
			documents.items.filter(
				/* A row that became a retention copy after its transfer stays in the
				   list with its id still ticked; it is not transferable any more. */
				doc => selectedIds.includes(doc._id) && !doc.isRetainedCopy
			),
		[documents.items, selectedIds]
	);

	const openTransferForSelection = useCallback(() => {
		if (!selectedDocs.length) return;
		setTransferDocs(selectedDocs);
	}, [selectedDocs]);

	useEffect(() => {
		onSelectionChange?.({
			documents: selectedDocs,
			openTransfer: openTransferForSelection,
			clear: clearDocuments,
		});
	}, [selectedDocs, openTransferForSelection, clearDocuments, onSelectionChange]);

	/**
	 * A committed transfer moves originals OUT of this list, so the page on
	 * screen is describing documents the reader no longer owns until it
	 * refetches. The drawer closes with it: its Custody and Integrity sections
	 * are scoped to the caller as owner, and `GET /vault-evidence/:id` answers
	 * 404 the moment that stops being true.
	 */
	const afterTransfer = useCallback(() => {
		setTransferDocs(null);
		setOpenDoc(null);
		clearDocuments();
		void refetchDocuments();
	}, [clearDocuments, refetchDocuments]);

	/**
	 * A committed checkout, or a re-vault, moves the row BETWEEN the two lists — so
	 * whichever one is on screen is now describing a document that is not in it. The
	 * drawer closes with it: its Custody and Integrity sections come from
	 * `GET /vault-evidence/:id`, and its footer offers acts the server has just started
	 * refusing.
	 */
	const afterCustodyChange = useCallback(() => {
		setCheckoutDoc(null);
		setRevaultDoc(null);
		setOpenDoc(null);
		clearDocuments();
		void refetchDocuments();
	}, [clearDocuments, refetchDocuments]);

	const sortKey = useMemo(
		() =>
			SORTS.find(s => s.sort === filters.sort && s.order === filters.order)?.key ??
			'recent',
		[filters.sort, filters.order]
	);

	const chooseSort = useCallback(
		(key: string) => {
			const next = SORTS.find(s => s.key === key) ?? SORTS[0];
			if (!next) return;
			setFilter('sort', next.sort);
			setFilter('order', next.order);
		},
		[setFilter]
	);

	const copyReference = useCallback(
		(doc: IVaultDocument) => {
			const reference = formatDisplayId(doc.displayId);
			navigator.clipboard.writeText(reference);
			successNotification(`${reference} copied`);
		},
		[successNotification]
	);

	/**
	 * A checked-out row's menu is a different menu, not the same one with things greyed
	 * out: the only acts left on that record are reading it and bringing it back.
	 * Transfer stays on the list, disabled with its reason, because its absence would
	 * read as a bug to somebody who had just used it on the row above.
	 */
	const kebabItems = useCallback(
		(doc: IVaultDocument) => {
			const checkedOut = doc.status === 'checked_out';
			if (checkedOut) {
				return [
					{ label: 'View details', onClick: () => setOpenDoc(doc) },
					{
						label: 'Re-vault',
						onClick: () => setRevaultDoc(doc),
						disabled: !!blocked,
						hint: blocked ? NEEDS_ADDON_CHECKOUT : undefined,
					},
					{
						label: 'Transfer ownership',
						onClick: () => setTransferDocs([doc]),
						disabled: true,
						hint: CHECKED_OUT_HINT,
					},
					{ label: 'Copy reference', onClick: () => copyReference(doc) },
				];
			}
			/* A retained copy is not ours to check out either — the original left with the
			   new owner, and the server's `ownerId` scope refuses it. A legal hold blocks
			   it for a different reason, so it gets a different sentence. */
			const held = !!doc.legalHold?.active;
			return [
				{ label: 'View details', onClick: () => setOpenDoc(doc) },
				{
					/* One document, not the checkbox selection: this menu belongs to
					   the row it hangs off, and transferring something else because
					   boxes were ticked elsewhere would be the worst kind of
					   surprise on an irreversible action. */
					label: 'Transfer ownership',
					onClick: () => setTransferDocs([doc]),
					disabled: !!blocked || !!doc.isRetainedCopy,
					hint: blocked
						? NEEDS_ADDON_TRANSFER
						: doc.isRetainedCopy
							? RETAINED_COPY_HINT
							: undefined,
				},
				{
					label: 'Check out',
					onClick: () => setCheckoutDoc(doc),
					disabled: !!blocked || !!doc.isRetainedCopy || held,
					hint: blocked
						? NEEDS_ADDON_CHECKOUT
						: doc.isRetainedCopy
							? RETAINED_COPY_CHECKOUT_HINT
							: held
								? LEGAL_HOLD_HINT
								: undefined,
				},
				{ label: 'Copy reference', onClick: () => copyReference(doc) },
			];
		},
		[blocked, copyReference]
	);

	/* THE HEADER CHECKBOX SELECTS WHAT A ROW CLICK COULD.
	   A retained copy's own checkbox is disabled — custody has already moved, so
	   it cannot be transferred — and select-all must not reach past that guard
	   and put one in the bulk bar. */
	const selectableIds = useMemo(
		() => documents.items.filter(doc => !doc.isRetainedCopy).map(doc => doc._id),
		[documents.items]
	);
	const allDocsChecked =
		selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id));

	const hasNoVaults = vaultIds.length === 0;

	return (
		<>
			{!hasNoVaults && (
				<div className="ctl">
					<span className="cl">Documents</span>
					<SearchBox
						value={typedSearch}
						onChange={setTypedSearch}
						placeholder="Search documents"
						title="Searches document name and reference"
					/>
					<select
						className="range"
						aria-label="Type"
						value={filters.type ?? ''}
						onChange={e =>
							setFilter('type', (e.target.value || null) as typeof filters.type)
						}
					>
						<option value="">Any type</option>
						<option value="pdf">PDF</option>
						<option value="docx">DOCX</option>
					</select>
					<select
						className="range"
						aria-label="Sort documents"
						value={sortKey}
						onChange={e => chooseSort(e.target.value)}
					>
						{SORTS.map(s => (
							<option key={s.key} value={s.key}>
								{s.label}
							</option>
						))}
					</select>
					{/* WHICH LIST, not a filter over one — see `IDocumentState`. It is the
					    `state=` query the server reads, and the default is what the drive
					    has always shown. */}
					<select
						className="range"
						aria-label="Custody state"
						value={filters.state}
						onChange={e => setFilter('state', e.target.value as IDocumentState)}
					>
						{STATES.map(s => (
							<option key={s.key} value={s.key}>
								{s.label}
							</option>
						))}
					</select>
					{extraControls}
					<Pager page={page} pages={pages} onChange={setPage} />
				</div>
			)}

			{!hasNoVaults && selectedIds.length > 0
				? bulkBar?.({
						selectedDocs,
						clear: clearDocuments,
						openTransfer: openTransferForSelection,
				  })
				: null}

			<div className="panel">
				<div className="ph">
					Documents
					{!hasNoVaults && documents.isLoaded && !documents.error && (
						<span className="r">{documents.total}</span>
					)}
				</div>

				{/* A failed load is never the empty state: "no documents" and "the
				    service did not answer" look identical in a table and mean
				    opposite things. */}
				{documents.error && (
					<div className="errbar">
						<span className="et">{documents.error}</span>
						<button
							className="reinv errbar__act"
							type="button"
							onClick={() => void refetchDocuments()}
						>
							Try again
						</button>
					</div>
				)}

				{hasNoVaults ? (
					/* A HINT, NOT AN EMPTY STATE. Nothing is missing — the reader
					   simply has not chosen yet — so it gets a one-line band rather
					   than the 44px-padded box that says "there is nothing here". */
					<div className="sv2-empty hint">Select a vault to see its documents.</div>
				) : (
					<>
						{documents.isLoading && !documents.isLoaded && (
							<div className="sv2-loading">Loading documents…</div>
						)}

						{documents.isLoaded && !documents.error && documents.items.length === 0 && (
							<div className="sv2-empty">
								{activeFilterCount
									? 'No documents match these filters.'
									: 'Documents arrive here automatically once every recipient has signed an agreement vaulted to this vault.'}
							</div>
						)}

						{documents.items.length > 0 && view === 'list' && (
							<div className="tscroll">
								<table className="stbl dtbl">
									<thead>
										<tr>
											<th className="ckcol">
												<input
													type="checkbox"
													checked={allDocsChecked}
													disabled={selectableIds.length === 0}
													onChange={() => toggleAllDocuments(selectableIds)}
													aria-label="Select all transferable documents on this page"
												/>
											</th>
											<th>Document</th>
											{!hideVaultColumn && <th>Vault</th>}
											<th>Signers</th>
											<th>Deposited</th>
											<th>Status</th>
											<th className="accol">Actions</th>
										</tr>
									</thead>
									<tbody>
										{documents.items.map(doc => {
											const signers = doc.signers ?? [];
											const shown = signers.slice(0, 2);
											/* The same two keys as the grid card: Enter opens the
											   drawer, Space picks the document without opening it —
											   prevented, or Space scrolls the page as well.
											   `target !== currentTarget` leaves the checkbox and the
											   kebab inside the row acting for themselves. */
											const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
												if (e.target !== e.currentTarget) return;
												if (e.key === 'Enter') setOpenDoc(doc);
												if (
													e.key === ' ' &&
													!doc.isRetainedCopy &&
													doc.status !== 'checked_out'
												) {
													e.preventDefault();
													toggleDocument(doc._id);
												}
											};
											return (
												<tr
													key={doc._id}
													className={selectedIds.includes(doc._id) ? 'sel' : ''}
													tabIndex={0}
													onClick={() => setOpenDoc(doc)}
													onKeyDown={onRowKey}
												>
													<td
														className="ckcol"
														onClick={e => e.stopPropagation()}
														role="presentation"
													>
														<input
															type="checkbox"
															aria-label={`Select ${doc.name}`}
															disabled={!!doc.isRetainedCopy || doc.status === 'checked_out'}
															title={
																doc.status === 'checked_out'
																	? CHECKED_OUT_HINT
																	: doc.isRetainedCopy
																		? RETAINED_COPY_HINT
																		: undefined
															}
															checked={selectedIds.includes(doc._id)}
															onChange={() => toggleDocument(doc._id)}
														/>
													</td>
													<td>
														<div className="nm">
															{doc.name}
															{doc.status === 'checked_out' && (
																<StatusChip verdict="ns">CHECKED OUT</StatusChip>
															)}
															{doc.isRetainedCopy && (
																/* Custody has moved; this is the copy the sender
																   kept. Saying so on the row is the whole point —
																   an unmarked row would claim ownership the
																   viewer no longer has. */
																<span className="retained" title="Transferred away — you kept a copy">
																	Retained copy
																</span>
															)}
															<LedgerChip doc={doc} />
														</div>
														<div className="fund">{formatDisplayId(doc.displayId)}</div>
													</td>
													{!hideVaultColumn && (
														<td>
															{doc.vault ? (
																/* The row opens the drawer; this cell goes to the
																   vault instead, so it has to keep the click. */
																<Link
																	className="vlink"
																	to={`/evault/${doc.vault._id}`}
																	onClick={e => e.stopPropagation()}
																>
																	{doc.vault.name}
																</Link>
															) : (
																'—'
															)}
														</td>
													)}
													<td>
														{shown.length ? (
															<>
																{shown.map(s => (
																	<div className="ph2" key={s._id} title={s.email}>
																		{s.name || s.email}
																	</div>
																))}
																{signers.length > 2 && (
																	<div className="fund">+{signers.length - 2}</div>
																)}
															</>
														) : (
															'—'
														)}
													</td>
													{/* `td.dep` puts the time on its OWN LINE, the way the
													    inbox deck's `td.mid` does. Without it the two spans
													    run together as "Aug 30, 202602:10 PM" — an inline
													    `.fund` next to a bare text node has nothing between
													    them, and the year and the hour are both digits, so
													    the join is invisible until you try to read it. */}
													<td className="dep" title={isoOf(doc.depositedAt)}>
														{dayOf(doc.depositedAt)}
														<span className="fund">{timeOf(doc.depositedAt)}</span>
													</td>
													<td>
														{/* From `status`, never a literal: the card upstream
														    printed "Signed" whatever the record said. A
														    checked-out record is a custody state rather than a
														    pass, so it gets the neutral chip the grid card and
														    the drawer give it — and the underscore in the
														    stored value is not something to print at a reader. */}
														<StatusChip
															verdict={doc.status === 'checked_out' ? 'ns' : 'ok'}
														>
															{doc.status.replace('_', ' ').toUpperCase()}
														</StatusChip>
													</td>
													<td className="accol">
														<Kebab items={kebabItems(doc)} />
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}

						{documents.items.length > 0 && view === 'grid' && (
							<div className="dgrid">
								{documents.items.map(doc => {
									const selected = selectedIds.includes(doc._id);
									const widths = previewLineWidths(doc._id);
									return (
										<div
											className="dcard"
											key={doc._id}
											role="button"
											tabIndex={0}
											aria-pressed={selected}
											data-selected={selected ? 'true' : 'false'}
											onClick={() => setOpenDoc(doc)}
											onKeyDown={e => {
												/* The checkbox and the kebab inside the card bubble their
												   own keystrokes up here; only the card's own are the
												   card's to act on. */
												if (e.target !== e.currentTarget) return;
												if (e.key === 'Enter') setOpenDoc(doc);
												if (
													e.key === ' ' &&
													!doc.isRetainedCopy &&
													doc.status !== 'checked_out'
												) {
													e.preventDefault();
													toggleDocument(doc._id);
												}
											}}
										>
											<div className="prev">
												<input
													type="checkbox"
													className="pck"
													aria-label={`Select ${doc.name}`}
													disabled={!!doc.isRetainedCopy || doc.status === 'checked_out'}
													title={
														doc.status === 'checked_out'
															? CHECKED_OUT_HINT
															: doc.isRetainedCopy
																? RETAINED_COPY_HINT
																: undefined
													}
													checked={selected}
													onClick={e => e.stopPropagation()}
													onChange={() => toggleDocument(doc._id)}
												/>
												{doc.thumbnailUrl ? (
													<img src={doc.thumbnailUrl} alt="" />
												) : (
													<div className="stand">
														<div className="ptitle">{doc.name}</div>
														{widths.map((w, i) => (
															<div
																// eslint-disable-next-line react/no-array-index-key
																key={`${doc._id}-${i}`}
																className="pline"
																style={{ width: `${w ?? 50}%` }}
															/>
														))}
													</div>
												)}
												<div className="pfoot">
													<StatusChip
														verdict={doc.status === 'checked_out' ? 'ns' : 'ok'}
													>
														{doc.status === 'checked_out' ? '' : '\u2713 '}
														{doc.status.replace('_', ' ').toUpperCase()}
													</StatusChip>
												</div>
											</div>
											<div className="cfoot">
												<span className={`fbadge ${doc.fileType}`}>
													{doc.fileType.toUpperCase()}
												</span>
												<div className="cmeta">
													<div className="nm">
														{doc.name}
														{doc.status === 'checked_out' && (
															<StatusChip verdict="ns">CHECKED OUT</StatusChip>
														)}
														{doc.isRetainedCopy && (
															<span className="retained" title="Transferred away — you kept a copy">
																Retained copy
															</span>
														)}
														<LedgerChip doc={doc} />
													</div>
													<div className="fund">{formatDisplayId(doc.displayId)}</div>
												</div>
												<span
													className="vkebab"
													onClick={e => e.stopPropagation()}
													role="presentation"
												>
													<Kebab items={kebabItems(doc)} />
												</span>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</>
				)}
			</div>

			{/* The drawer and the transfer modal live HERE rather than in either
			    deck because both are opened from a document — a row, a card, a
			    kebab — and this component is the only place that owns documents.
			    The drive's bulk bar reaches this same modal through
			    `onSelectionChange`, so there is exactly one of it on the page. */}
			<DocumentDrawer
				api={api}
				document={openDoc}
				open={!!openDoc}
				blocked={blocked}
				onClose={() => setOpenDoc(null)}
				onTransfer={doc => setTransferDocs([doc])}
				onCheckout={doc => setCheckoutDoc(doc)}
				onRevault={doc => setRevaultDoc(doc)}
			/>

			{!!transferDocs?.length && (
				<TransferModal
					api={api}
					documents={transferDocs}
					transferPrice={transferPrice}
					onClose={() => setTransferDocs(null)}
					onTransferred={afterTransfer}
					/* A 409 means the list is describing a state that no longer
					   holds; the modal stays up with the server's sentence on it. */
					onRefresh={() => void refetchDocuments()}
					onUnentitled={() => {
						setTransferDocs(null);
						setUpsellOpen(true);
					}}
				/>
			)}

			{!!checkoutDoc && (
				<CheckoutModal
					api={api}
					document={checkoutDoc}
					onClose={() => setCheckoutDoc(null)}
					onCheckedOut={afterCustodyChange}
					/* A 409 means the list is describing a state that no longer holds;
					   the modal stays up with the server's sentence on it. */
					onRefresh={() => void refetchDocuments()}
					onUnentitled={() => {
						setCheckoutDoc(null);
						setUpsellOpen(true);
					}}
				/>
			)}

			{!!revaultDoc && (
				<RevaultModal
					api={api}
					document={revaultDoc}
					vaults={vaults}
					onClose={() => setRevaultDoc(null)}
					onRevaulted={afterCustodyChange}
					onUnentitled={() => {
						setRevaultDoc(null);
						setUpsellOpen(true);
					}}
				/>
			)}

			{upsellOpen && <AddonUpsellModal open onClose={() => setUpsellOpen(false)} />}
		</>
	);
};

export default DocumentPanel;
